import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import Button from '../../../BaseComponents/Button';
import Modal from '../../../BaseComponents/Modal';
import Select from '../../../BaseComponents/Select';
import MultiSelect from '../../../BaseComponents/MultiSelect';
import TextInput from '../../../BaseComponents/TextInput';
import TextArea from '../../../BaseComponents/TextArea';
import './SupplementFormModal.scss';

import UploadPhoto from '../../../UploadPhoto';

import { Supplement, createSupplement, deleteSupplement, updateSupplement,getSupplementsList } from '../../../../api/supplements';
import { getBiomarkersList } from '../../../../api/biomarkers';
import { ListBiomarkerResponseElement } from '../../../../types/apiGeneratedTypes';

export interface SupplementFormValues {
  image: string;
  name: string;
  description: string;
  recommended_dosage: string;
  dosage_unit: { key: string; value: string };
  instructions: string;
  interactions: string;
  biomarkers: { key: string; value: string }[];
}

interface SupplementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplementToEdit?: Supplement | null;
  mode: 'create' | 'edit';
  onSupplementCreated?: () => void;
}

const dosageUnits: { key: string; value: string }[] = [
  { key: 'mg', value: 'mg' },
  { key: 'µg', value: 'µg' },
  { key: 'g', value: 'g' },
  { key: 'mg_elemental', value: 'mg elemental' },
  { key: 'IU', value: 'IU' },
  { key: 'CFU', value: 'CFU' },
  { key: 'FU', value: 'FU' },
  { key: 'USP_units', value: 'USP units' },
  { key: '%', value: '%' },
  { key: 'ratio_extract', value: 'ratio extract' },
  { key: 'ppm', value: 'ppm' },
  { key: 'ml', value: 'ml' },
  { key: 'drops', value: 'drops' },
  { key: 'tsp', value: 'tsp' },
  { key: 'tbsp', value: 'tbsp' },
  { key: 'kcal', value: 'kcal' },
  { key: 'mcg', value: 'mcg' },
  { key:'per_day', value:'per day'}
];

const SupplementFormModal: React.FC<SupplementFormModalProps> = ({
  isOpen,
  onClose,
  supplementToEdit,
  mode,
  onSupplementCreated
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [biomarkerOptions, setBiomarkerOptions] = useState<{ key: string; value: string }[]>([]);
  const [isLoadingBiomarkers, setIsLoadingBiomarkers] = useState(false);
  const [biomarkerError, setBiomarkerError] = useState<string | null>(null);
  const [deleteModal,setDeleteModal] = useState<boolean>(false);
  const [isEdit , setIsEdit] = useState<boolean>(true)

  // Fetch biomarkers when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBiomarkers();
    }
  }, [isOpen]);

  useEffect(() => {
    setIsEdit((prev) => prev && mode !== 'create');
  }, [mode]);

  const fetchBiomarkers = async () => {
    try {
      setIsLoadingBiomarkers(true);
      setBiomarkerError(null);
      const response = await getBiomarkersList();
      console.log('Biomarkers API response:', response);
      
      const options = response.biomarkers.map((biomarker: ListBiomarkerResponseElement) => ({
        key: biomarker.id.toString(),
        value: biomarker.name
      }));
      
      console.log('Mapped biomarker options:', options);
      setBiomarkerOptions(options);
    } catch (error) {
      console.error('Error fetching biomarkers:', error);
      setBiomarkerError('Failed to load biomarkers. Please try again.');
      // Keep empty array if fetch fails
      setBiomarkerOptions([]);
    } finally {
      setIsLoadingBiomarkers(false);
    }
  };

  const getInitialValues = (): SupplementFormValues => {
    if (mode === 'edit' && supplementToEdit) {
      // Map existing biomarker IDs to biomarker objects with names if available
      const biomarkers = supplementToEdit.biomarker_ids ? 
        supplementToEdit.biomarker_ids.map(bioId => {
          const biomarker = biomarkerOptions.find(option => option.key === bioId.toString());
          return biomarker || { key: bioId.toString(), value: bioId.toString() };
        }) : 
        [];

      return {
        image: supplementToEdit.image || '',
        name: supplementToEdit.supplement_name || '',
        description: supplementToEdit.description || '',
        recommended_dosage: supplementToEdit.recommended_dosage ? supplementToEdit.recommended_dosage.toString() : '500',
        dosage_unit: supplementToEdit.dosage_units ? 
          { key: supplementToEdit.dosage_units, value: supplementToEdit.dosage_units } : 
          { key: 'mg', value: 'mg' },
        instructions: supplementToEdit.instructions_for_taking || '',
        interactions: supplementToEdit.interactions_with_medications || '',
        biomarkers
      };
    }

    return {
      image: '',
      name: '',
      description: '',
      recommended_dosage: '500',
      dosage_unit: { key: 'mg', value: 'mg' },
      instructions: '',
      interactions: '',
      biomarkers: [] 
    };
  };

  const validate = (values: SupplementFormValues) => {
    const errors: any = {};

    if (!values.name || values.name.trim() === '') {
      errors.name = 'Supplement name is required';
    }

    if (!values.description || values.description.trim() === '') {
      errors.description = 'Description is required';
    }

    if (!values.instructions || values.instructions.trim() === '') {
      errors.instructions = 'Instructions for taking is required';
    }

    if (!values.dosage_unit || !values.dosage_unit.value) {
      errors.dosage_unit = 'Dosage unit is required';
    }

    if (values.name && values.name.length > 500) {
      errors.name = 'Name must be 500 characters or less';
    }
    if (values.recommended_dosage && isNaN(parseFloat(values.recommended_dosage))) {
      errors.recommended_dosage = 'Recommended dosage must be a valid number';
    }

    if (values.instructions && values.instructions.length > 200) {
      errors.instructions = 'Instructions must be 200 characters or less';
    }

    if (values.interactions && values.interactions.length > 200) {
      errors.interactions = 'Interactions must be 200 characters or less';
    }

    if (values.biomarkers && values.biomarkers.length > 0) {
      const invalidBiomarkers = values.biomarkers.filter(bio => 
        !bio || !bio.key || isNaN(parseInt(bio.key))
      );
      if (invalidBiomarkers.length > 0) {
        errors.biomarkers = 'All biomarkers must have valid IDs';
      }
    }

    return errors;
  };

  const handleSubmit = async (values: SupplementFormValues, { setSubmitting }: any) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const apiData = {
        supplement_name: values.name.trim(),
        description: values.description.trim(),
        instructions_for_taking: values.instructions.trim(),
        image: (() => {
          if (!values.image) {
            return '';
          }
          
          if (values.image.startsWith('static/')) {
            return values.image;
          }
          
          if (values.image.includes('https://') || values.image.includes('http://')) {
            const staticPathMatch = values.image.match(/static\/[^?]+/);
            return staticPathMatch ? staticPathMatch[0] : values.image;
          }
          
          return values.image;
        })(),
        recommended_dosage: parseFloat(values.recommended_dosage) || 0,
        dosage_units: values.dosage_unit.value,
        interactions_with_medications: values.interactions.trim() || '',
        biomarker_ids: (() => {
          if (!values.biomarkers || values.biomarkers.length === 0) {
            return [];
          }
          
          const validBiomarkers = values.biomarkers
            .filter(bio => bio && bio.key && bio.key.trim() !== '')
            .map(bio => parseInt(bio.key.trim()))
            .filter((num): num is number => !isNaN(num));
          
          return validBiomarkers;
        })()
      };
      
      
             if (mode === 'create') {
         await createSupplement(apiData);
         
         toast.success('Supplement created successfully!');
         
         // Dispatch event to notify other components that a supplement was created
         window.dispatchEvent(new Event('supplement-created'));
         
         if (onSupplementCreated) {
           onSupplementCreated();
         }
         
       } else if (supplementToEdit) {
         
         await updateSupplement({
           supplement_id: supplementToEdit.id!,
           ...apiData
         });
         
         toast.success('Supplement updated successfully!');
         
         if (onSupplementCreated) {
           onSupplementCreated();
         }
       }
      
      
      onClose();
      
         } catch (err: any) {
       console.error('Error saving supplement:', err);
       const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to save supplement';
       setError(errorMessage);
       toast.error(`Error: ${errorMessage}`);
     } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDeletButton = async (id: number) => {
    await deleteSupplement(id)
    getSupplementsList()
    if (onSupplementCreated) {
      onSupplementCreated();
    }
    onClose();
    setDeleteModal(false)
  }

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
              title={mode === 'create' ? 'Add New Supplement' : `Edit Supplement: ${supplementToEdit?.supplement_name || ''}`}
      width="800px"
    >
      <Formik
        initialValues={getInitialValues()}
        validate={validate}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validateOnSubmit={true}
      >
        {({ values, errors, isSubmitting, handleChange, setFieldValue, submitCount, submitForm }) => (
          <Form className="supplement-form" onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

              <div className="form-field">
                <label className="field-label">Image</label>
                <div className="upload-photo-wrapper">
                  <UploadPhoto
                    defaultPhoto={supplementToEdit?.image || undefined}
                    onPhotoChange={(photo: string) => setFieldValue('image', photo)}
                    putToStaticFolder={true}
                    isDisable={isEdit}
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="field-label">Supplement Name</label>
                <TextInput
                  name="name"
                  placeholder="Enter supplement name"
                  value={values.name}
                  onChange={handleChange}
                  isError={submitCount > 0 && !!errors.name}
                  errorMessage={submitCount > 0 ? errors.name : undefined}
                  isDisabled={isEdit}
                />
                <div className="char-limit">Max 500 characters</div>
              </div>

              <div className="form-field">
                <label className="field-label">Description</label>
                <TextArea
                  name="description"
                  placeholder="Enter text"
                  value={values.description}
                  onChange={handleChange}
                  isError={submitCount > 0 && !!errors.description}
                  errorMessage={submitCount > 0 ? errors.description : undefined}
                  isDisabled={isEdit}
                />
                
              </div>

              <div className="form-field">
                <label className="field-label">Recommended Dosage (optional)</label>
                <div className="dosage-row">
                  <TextInput
                    name="recommended_dosage"
                    placeholder="500"
                    value={values.recommended_dosage}
                    onChange={handleChange}
                    isError={submitCount > 0 && !!errors.recommended_dosage}
                    errorMessage={submitCount > 0 ? errors.recommended_dosage : undefined}
                    isDisabled={isEdit}
                  />
                  <Select
                    name="dosage_unit"
                    options={dosageUnits}
                    selectedOption={values.dosage_unit}
                    onChange={(option: { key: string; value: string }) => setFieldValue('dosage_unit', option)}
                    placeholder="mg"
                    isError={false}
                    isDisable={isEdit}
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="field-label">Instructions for Taking</label>
                <TextArea
                  name="instructions"
                  placeholder="Enter text"
                  value={values.instructions}
                  onChange={handleChange}
                  isError={submitCount > 0 && !!errors.instructions}
                  errorMessage={submitCount > 0 ? errors.instructions : undefined}
                  isDisabled={isEdit}
                />
                <div className="char-limit">Max 200 characters – when/how to take</div>
              </div>

              <div className="form-field">
                <label className="field-label">Interactions with Other Medications (optional)</label>
                <TextArea
                  name="interactions"
                  placeholder="Enter text"
                  value={values.interactions}
                  onChange={handleChange}
                  isError={submitCount > 0 && !!errors.interactions}
                  errorMessage={submitCount > 0 ? errors.interactions : undefined}
                  isDisabled={isEdit}
                />
                <div className="char-limit">Max 200 characters – flag any known risks</div>
              </div>

              <div className="form-field">
                <label className="field-label">Relevant to Biomarker or Conditions (optional)</label>
                {isLoadingBiomarkers ? (
                  <div className="biomarkers-loading">
                    Loading biomarkers...
                  </div>
                ) : biomarkerError ? (
                  <div className="biomarkers-error">
                    {biomarkerError}
                    <button 
                      type="button" 
                      className="retry-button" 
                      onClick={fetchBiomarkers}
                    >
                      Retry
                    </button>
                  </div>
                ) : biomarkerOptions.length > 0 ? (
                  <MultiSelect
                    name="biomarkers"
                    options={biomarkerOptions}
                    selectedOptions={values.biomarkers || []}
                    disabled={isEdit}
                    onChange={(option: { key: string; value: string }, name: string) => {
                      const currentBiomarkers = values.biomarkers || [];
                      const existingIndex = currentBiomarkers.findIndex(bio => bio && bio.key === option.key);
                      
                      if (existingIndex >= 0) {
                        // Remove if already selected
                        const newBiomarkers = currentBiomarkers.filter((_, index) => index !== existingIndex);
                        setFieldValue('biomarkers', newBiomarkers);
                      } else {
                        // Add if not selected
                        const newBiomarkers = [...currentBiomarkers, option];
                        setFieldValue('biomarkers', newBiomarkers);
                      }
                      
                      // Debug logging
                      console.log('Biomarker selection changed:', {
                        option,
                        currentBiomarkers,
                        existingIndex,
                        newBiomarkers: existingIndex >= 0 
                          ? currentBiomarkers.filter((_, index) => index !== existingIndex)
                          : [...currentBiomarkers, option]
                      });
                    }}
                    placeholder="Choose biomarkers (click to add/remove)"
                    isError={false}
                  />
                ) : (
                  <div className="biomarkers-error">
                    No biomarkers available
                  </div>
                )}
                <div className="char-limit">
                  Selected: {values.biomarkers.length > 0 ? values.biomarkers.map(bio => bio.value).join(', ') : 'None'}
                </div>
              </div>

            <div className="form-actions">
              {mode === 'create' ? <Button
                text={mode === 'create' ? 'Add Supplement' : 'Update Supplement'}
                type="submit"
                isLoading={isLoading}
                isDisabled={isSubmitting || !values.name || !values.description || !values.instructions || !values.dosage_unit.value}
              /> : 
              <>
                <Button
                  text={!isEdit ? 'Save' : 'Edit'}
                  type="button"
                  isLoading={isLoading}
                  onClick={() => {
                    if (isEdit) {
                      setIsEdit(false);
                    } else {
                      submitForm();
                    }
                  }}
                />
                <Button
                  text={'Delete Supplement'}
                  isLoading={isLoading}
                  color='black'
                  variant='delete'
                  isDisabled={isSubmitting || !values.name || !values.description || !values.instructions || !values.dosage_unit.value}
                  onClick={() => setDeleteModal(true)}
                />
              </>}
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
      <Modal isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        title={""}
        width="650px">
        <h3>Are you sure you want to delete this supplement?</h3>
        <div className="modal-action">
          <Button
            text={'Yes'}
            type="submit"
            isLoading={isLoading}
            onClick={() => {handleDeletButton(supplementToEdit?.id ?? 0)}}
          />
          <Button
            text={'No'}
            type="submit"
            isLoading={isLoading}
            onClick={() => setDeleteModal(false)}
          />
        </div>
      </Modal>
    </>
  );
};

export default SupplementFormModal;
